import type {
  FrameworkSnapshot,
  ResolvedPartnerCapabilityMapping,
  ResolvedPartnerCapabilityMappingNode
} from "./types";

export interface PartnerCapabilityReverseIndex {
  byDomainWorkblockId: Record<string, ResolvedPartnerCapabilityMappingNode[]>;
  byBusinessModuleId: Record<string, ResolvedPartnerCapabilityMappingNode[]>;
  bySharedCapabilityId: Record<string, ResolvedPartnerCapabilityMappingNode[]>;
}

function push(
  index: Record<string, ResolvedPartnerCapabilityMappingNode[]>,
  id: string,
  node: ResolvedPartnerCapabilityMappingNode
): void {
  (index[id] ??= []).push(node);
}

/** Build the GroundWorks-to-partner direction from the one authored forward map. */
export function buildPartnerCapabilityReverseIndex(
  mapping: ResolvedPartnerCapabilityMapping
): PartnerCapabilityReverseIndex {
  const result: PartnerCapabilityReverseIndex = {
    byDomainWorkblockId: {},
    byBusinessModuleId: {},
    bySharedCapabilityId: {}
  };
  const areaOrder = new Map(
    mapping.nodes
      .filter((node) => node.kind !== "capability")
      .map((node) => [node.id, node.order])
  );
  const capabilities = mapping.nodes
    .filter((node) => node.kind === "capability")
    .sort((left, right) =>
      (areaOrder.get(left.parentId ?? "") ?? Number.MAX_SAFE_INTEGER)
        - (areaOrder.get(right.parentId ?? "") ?? Number.MAX_SAFE_INTEGER)
      || left.order - right.order
      || left.id.localeCompare(right.id)
    );
  for (const node of capabilities) {
    for (const id of node.groundworks.domainWorkblockIds) push(result.byDomainWorkblockId, id, node);
    for (const id of node.groundworks.businessModuleIds) push(result.byBusinessModuleId, id, node);
    for (const id of node.groundworks.sharedCapabilityIds) push(result.bySharedCapabilityId, id, node);
  }
  return result;
}

export function defaultPartnerCapabilityMapping(
  snapshot: FrameworkSnapshot
): ResolvedPartnerCapabilityMapping | undefined {
  const mappings = snapshot.partnerCapabilityMappings ?? [];
  return mappings.find(({ id }) => id === snapshot.defaultPartnerCapabilityMappingId);
}
